import { Directive, signal, WritableSignal } from '@angular/core';
import {
  apply,
  form,
  PathKind,
  SchemaOrSchemaFn,
  SchemaPath,
  SchemaPathRules,
  SchemaPathTree,
  validate,
} from '@angular/forms/signals';
import { AppError } from '../models/api.model';
import { BaseComponent } from './base.component';
import { SafeOptions } from '../models/base.model';

/**
 * 💡 Typescript Expert Tip: Tránh sử dụng 'any' bằng cách sử dụng Generics và Type Guards.
 * Chúng ta sử dụng 'T extends object' để đảm bảo có thể duyệt qua các keys.
 */
@Directive()
export abstract class BaseFormComponent extends BaseComponent {
  /** Signal lưu trữ các lỗi validation từ API trả về cho từng field */
  public fieldErrors = signal<Record<string, string>>(
    {} as Record<string, string>,
  );

  /** Lưu trữ giá trị input tại thời điểm xảy ra lỗi để so sánh (dưới dạng JSON string) */
  private errorValues: Record<string, string> = {};

  /**
   * Cấu hình trung tâm chứa các Error Messages translation keys.
   */
  protected readonly VALIDATION_MESSAGES: Record<string, string> = {
    required: 'VALIDATION.REQUIRED',
    email: 'VALIDATION.EMAIL',
    minlength: 'VALIDATION.MINLENGTH',
    maxlength: 'VALIDATION.MAXLENGTH',
    pattern: 'VALIDATION.PATTERN',
    min: 'VALIDATION.MIN',
    max: 'VALIDATION.MAX',
  };

  /**
   * Wrapper Handler dùng chung cho mọi thao tác Submit Form.
   * Tự động quản lý Loading, Toast lỗi global và Mapping lỗi từng Field.
   */
  protected async handleSubmit(
    action: () => Promise<void> | void,
    options: SafeOptions = {},
  ): Promise<void> {
    if (this.isBusy()) return; // Chống click lặp lại

    this.resetFieldErrors();

    await this.safeRun(
      async () => {
        await action();
      },
      {
        ...options,
        onError: (err) => {
          this.mapFieldErrors(err);
          options.onError?.(err);
        },
      },
    );
  }

  private mapFieldErrors(err: unknown): void {
    if (err instanceof AppError && err.fieldErrors) {
      this.fieldErrors.set(err.fieldErrors);
      // Lưu lại giá trị hiện tại của form để biết khi nào user thay đổi
      this.errorValues = { ...this.errorValues }; // Placeholder logic - will be updated per field
    }
  }

  /** Xoá lỗi của một field cụ thể */
  protected clearFieldError(fieldName: string): void {
    if (this.fieldErrors()[fieldName]) {
      const newErrors = { ...this.fieldErrors() };
      delete newErrors[fieldName];
      this.fieldErrors.set(newErrors);
      delete this.errorValues[fieldName];
    }
  }

  /** Reset toàn bộ lỗi validation */
  public resetFieldErrors() {
    this.fieldErrors.set({} as Record<string, string>);
    this.errorValues = {};
  }

  /**
   * 🛡️ Validator dùng cho Signal Forms để lắng nghe lỗi từ server.
   * Kết nối field trong form với signal fieldErrors của BaseFormComponent.
   */
  protected serverErrorValidator<T extends object>(
    fieldName: keyof T & string,
    model: WritableSignal<T>,
  ) {
    return () => {
      const errorMsg = this.fieldErrors()[fieldName];
      if (!errorMsg) return undefined;

      // Lấy giá trị hiện tại trực tiếp từ signal của model
      const currentValue = model()[fieldName];

      // Nếu chưa lưu giá trị lỗi, hãy lưu lại (lần đầu tiên phát hiện lỗi)
      if (!(fieldName in this.errorValues)) {
        this.errorValues[fieldName] = JSON.stringify(currentValue);
      }

      // So sánh: Nếu giá trị đã thay đổi so với lúc bị lỗi -> Xoá lỗi
      if (this.errorValues[fieldName] !== JSON.stringify(currentValue)) {
        // Đợi một chút để tránh việc cập nhật signal ngay trong quá trình compute validation
        setTimeout(() => this.clearFieldError(fieldName), 0);
        return undefined;
      }

      return { kind: 'serverError', message: errorMsg };
    };
  }

  protected bindServerErrors<T extends object>(
    rootPath: SchemaPathTree<T, PathKind.Root>,
    model: WritableSignal<T>,
  ): void {
    const keys = Object.keys(model()) as Array<keyof T & string>;

    keys.forEach((key) => {
      // 💡 Typescript Expert: Cast rootPath sang Record để có thể index an toàn bằng key
      const path = (
        rootPath as unknown as {
          [K in keyof T]: SchemaPathTree<T[K], PathKind.Child>;
        }
      )[key];
      if (path) {
        validate(
          path as unknown as SchemaPath<
            T[typeof key],
            SchemaPathRules.Supported,
            PathKind.Child
          >,
          this.serverErrorValidator(key, model),
        );
      }
    });
  }

  /**
   * Tạo form với server error binding tự động.
   * Chấp nhận cả Schema object (từ `schema()`) lẫn raw callback.
   *
   * @example
   * readonly myForm = this.formWithServerErrors(this.model, mySchema);
   */
  protected formWithServerErrors<T extends object>(
    model: WritableSignal<T>,
    rules: SchemaOrSchemaFn<T>,
  ) {
    return form(model, (s: SchemaPathTree<T, PathKind.Root>) => {
      apply(s as unknown as SchemaPath<T>, rules);
      this.bindServerErrors(s, model);
    });
  }

  /** Lấy Translation Key cho lỗi */
  public getErrorMessage(
    errors: Record<string, unknown> | null | undefined,
  ): string | null {
    if (!errors) return null;

    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return null;

    const firstError = errorKeys[0];
    return this.VALIDATION_MESSAGES[firstError] || 'VALIDATION.INVALID';
  }
}
